import type { NumberFormatValues } from 'react-number-format';
import { useDispatch } from 'react-redux';

import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import {
  NumericInput,
  NumericInputProps,
} from '@opendoor/cinderblocks/growth/novo/inputs/NumericFormat';

import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import { NumberRangeQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation } from 'src/lib/flow/validation';
import { changeAnswers } from 'src/store/sellerInput';

type NumberRangeInputFieldProps = {
  question: NumberRangeQuestion;
  value?: {
    min?: string;
    max?: string;
  };
  showValidation?: boolean;
};

const NumberRangeInputField: React.FC<NumberRangeInputFieldProps> = ({
  question,
  showValidation,
  value,
}) => {
  const dispatch = useDispatch();
  const { key, prefix, placeholder, maxLabel, minLabel } = question;
  let hasError = false;
  let errorTextMin: string | undefined | null;
  let errorTextMax: string | undefined | null;
  if (showValidation) {
    const result = checkValidation(question, value);
    hasError = !result.isValid;
    if ('type' in result && result.type == 'min_or_max_invalid') {
      errorTextMax = getErrorMessage(result.max);
      errorTextMin = getErrorMessage(result.min);
    } else {
      errorTextMin = getErrorMessage(result);
    }
  }

  // Separate functions to avoid confusion between min and max
  const minValueOnChange = (values: NumberFormatValues) => {
    let min: string | number | undefined = values.floatValue ?? undefined;
    if (typeof min == 'number' && !Number.isNaN(min)) {
      min = `${min}`;
    } else {
      min = undefined;
    }

    const answer = {
      min,
      max: value?.max ?? undefined,
    };
    dispatch(changeAnswers([{ key, answer }]));
  };

  const maxValueOnChange = (values: NumberFormatValues) => {
    let max: string | number | undefined = values.floatValue ?? undefined;
    if (typeof max == 'number' && !Number.isNaN(max)) {
      max = `${max}`;
    } else {
      max = undefined;
    }

    const answer = {
      max,
      min: value?.min ?? undefined,
    };
    dispatch(changeAnswers([{ key, answer }]));
  };

  return (
    <>
      <Field<NumericInputProps>
        label={minLabel}
        errorText={errorTextMin}
        InputComponent={NumericInput}
        input={{
          id: `${key}-min`,
          error: hasError,
          name: key,
          value: value?.min ? value.min.toString() : undefined,
          thousandSeparator: ',',
          onValueChange: minValueOnChange,
          type: 'text',
          formattedPrefix: prefix,
          placeholder,
        }}
      />
      <Field<NumericInputProps>
        label={maxLabel}
        errorText={errorTextMax}
        InputComponent={NumericInput}
        input={{
          id: `${key}-max`,
          error: hasError,
          name: key,
          value: value?.max ? value.max.toString() : undefined,
          thousandSeparator: ',',
          onValueChange: maxValueOnChange,
          type: 'text',
          formattedPrefix: prefix,
          placeholder: placeholder,
        }}
      />
    </>
  );
};

export default NumberRangeInputField;
